import { PointLight } from 'three';
import { settings } from '../config/settings.js';
import { damp } from '../utils/math.js';

const DEFAULT_POOL_SIZE = 6;

/**
 * A fixed set of dynamic point lights shared by every ability.
 *
 * The lights are created once and stay in the scene forever — adding or
 * removing a light changes the lighting program's cache key and forces three to
 * recompile *every* material, which is the classic cause of a hitch when a VFX
 * spawns. Unused lights simply sit at zero intensity.
 */
export class LightPool {
  constructor(scene, capacity = DEFAULT_POOL_SIZE) {
    this.lights = [];
    this.misses = 0;
    const poolSize = Math.max(1, Math.floor(Number(capacity) || DEFAULT_POOL_SIZE));
    for (let i = 0; i < poolSize; i++) {
      const light = new PointLight(0xffffff, 0, 10, 2);
      light.castShadow = false;
      light.intensity = 0;
      light.visible = true;
      light.position.set(0, -999, 0);
      scene.add(light);
      this.lights.push({ light, inUse: false, target: 0 });
    }
  }

  /** @returns {object|null} a handle, or null when the pool is exhausted */
  acquire() {
    for (const entry of this.lights) {
      if (!entry.inUse) {
        entry.inUse = true;
        entry.target = 0;
        entry.light.intensity = 0;
        if (!Number.isFinite(entry.light.position.x) || !Number.isFinite(entry.light.position.y) || !Number.isFinite(entry.light.position.z)) {
          entry.light.position.set(0, -999, 0);
        }
        return entry;
      }
    }
    this.misses++;
    return null;
  }

  /**
   * Drive an acquired light. Intensity/​distance are eased toward the target so
   * editor changes and ability fades never pop.
   */
  set(entry, position, color, intensity, distance, dt) {
    if (!entry) return;
    if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y) || !Number.isFinite(position.z)) {
      entry.target = 0;
      entry.light.intensity = 0;
      entry.light.position.set(0, -999, 0);
      return;
    }
    entry.light.position.copy(position);
    entry.light.color.copy(color);
    entry.target = (Number.isFinite(intensity) ? intensity : 0) * settings.global.lightIntensity;
    entry.light.intensity = damp(entry.light.intensity, entry.target, 0.0005, dt);
    entry.light.distance = (Number.isFinite(distance) ? distance : 0) * settings.global.lightRadius;
  }

  release(entry) {
    if (!entry) return;
    entry.inUse = false;
    entry.target = 0;
  }

  /** Fade released lights out instead of cutting them. */
  update(dt) {
    for (const entry of this.lights) {
      if (!entry.inUse && entry.light.intensity > 0.001) {
        entry.light.intensity = damp(entry.light.intensity, 0, 0.0001, dt);
      }
    }
  }

  reset() {
    for (const entry of this.lights) {
      entry.inUse = false;
      entry.target = 0;
      entry.light.intensity = 0;
      entry.light.position.set(0, -999, 0);
    }
  }

  dispose() {
    for (const entry of this.lights) entry.light.parent?.remove(entry.light);
    this.lights.length = 0;
  }
}
